/**
 * System Settings Component
 * Coláiste Lurgan Management System - General system configuration
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';
import { Loader2, Save } from 'lucide-react';
import api from '../../lib/api';

export function SystemSettings() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [travelPrice, setTravelPrice] = useState('');
  const [originalTravelPrice, setOriginalTravelPrice] = useState('');
  const [stripeDepositFee, setStripeDepositFee] = useState('');
  const [originalStripeDepositFee, setOriginalStripeDepositFee] = useState('');

  // Fetch current system settings
  useEffect(() => {
    fetchSystemSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSystemSettings = async () => {
    try {
      setLoading(true);
      const data = await api.systemSettings.get();
      const price = data.travel_price || '0.00';
      const stripeFee = data.stripe_deposit_fee || '0.00';
      setTravelPrice(price);
      setOriginalTravelPrice(price);
      setStripeDepositFee(stripeFee);
      setOriginalStripeDepositFee(stripeFee);
    } catch (err) {
      console.error('Error fetching system settings:', err);
      toast({
        variant: 'destructive',
        title: t('settings.system.fetchError', 'Failed to load system settings'),
        description: err.response?.data?.message || err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Validate travel price
      const priceValue = parseFloat(travelPrice);
      if (isNaN(priceValue) || priceValue < 0) {
        toast({
          variant: 'destructive',
          title: t('settings.system.invalidPrice', 'Please enter a valid price (0 or greater)'),
        });
        return;
      }

      // Validate stripe deposit fee
      const stripeFeeValue = parseFloat(stripeDepositFee);
      if (isNaN(stripeFeeValue) || stripeFeeValue < 0) {
        toast({
          variant: 'destructive',
          title: t('settings.system.invalidStripeFee', 'Please enter a valid Stripe fee (0 or greater)'),
        });
        return;
      }

      await api.systemSettings.update({
        travel_price: travelPrice,
        stripe_deposit_fee: stripeDepositFee
      });

      setOriginalTravelPrice(travelPrice);
      setOriginalStripeDepositFee(stripeDepositFee);

      toast({
        variant: 'success',
        title: t('settings.system.saveSuccess', 'System settings updated successfully'),
        description: t('settings.system.saveSuccessDescription', 'Settings have been updated.'),
      });
    } catch (err) {
      console.error('Error saving system settings:', err);
      toast({
        variant: 'destructive',
        title: t('settings.system.saveError', 'Failed to save system settings'),
        description: err.response?.data?.message || err.message,
      });
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = travelPrice !== originalTravelPrice || stripeDepositFee !== originalStripeDepositFee;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>
            {t('settings.system.title', 'System Settings')}
          </CardTitle>
          <CardDescription>
            {t('settings.system.description', 'Configure general system settings and pricing')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Travel Price Setting */}
          <div className="space-y-2">
            <Label htmlFor="travelPrice">
              {t('settings.system.travelPrice', 'Travel Price')}
            </Label>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground font-semibold">€</span>
              <Input
                id="travelPrice"
                type="number"
                min="0"
                step="0.01"
                value={travelPrice}
                onChange={(e) => setTravelPrice(e.target.value)}
                className="max-w-xs"
                placeholder="0.00"
              />
            </div>
            <p className="text-sm text-muted-foreground">
              {t('settings.system.travelPriceHelp',
                'This amount will be automatically added to the balance of students who require travel arrangements. Changes only affect students in upcoming courses.'
              )}
            </p>
          </div>

          {/* Stripe Deposit Fee Setting */}
          <div className="space-y-2">
            <Label htmlFor="stripeDepositFee">
              {t('settings.system.stripeDepositFee', 'Stripe Deposit Fee')}
            </Label>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground font-semibold">€</span>
              <Input
                id="stripeDepositFee"
                type="number"
                min="0"
                step="0.01"
                value={stripeDepositFee}
                onChange={(e) => setStripeDepositFee(e.target.value)}
                className="max-w-xs"
                placeholder="0.00"
              />
            </div>
            <p className="text-sm text-muted-foreground">
              {t('settings.system.stripeDepositFeeHelp',
                'This fee will be automatically subtracted from Stripe deposit amounts for display purposes. The original transaction amount is preserved in the database.'
              )}
            </p>
          </div>

          {/* Save Button */}
          <div className="flex items-center gap-3">
            <Button
              onClick={handleSave}
              disabled={!hasChanges || saving}
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('settings.system.saving', 'Saving...')}
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {t('settings.system.save', 'Save Changes')}
                </>
              )}
            </Button>

            {hasChanges && !saving && (
              <span className="text-sm text-muted-foreground">
                {t('settings.system.unsavedChanges', 'You have unsaved changes')}
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default SystemSettings;
